import { useState } from 'react';
import type { EventTemplateBindingItem, Template, TemplatePreviewResponse } from '@/api/templates';
import { templatesApi } from '@/api/templates';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChannelName,
  EmptyState,
  LoadingState,
} from '@/components/ui';
import { DispatchModeBadge } from '@/features/templates/dispatchMode';
import { ResolvedTemplatePreview } from './ResolvedTemplatePreview';
import { ChevronDown, ChevronRight, Eye } from 'lucide-react';

interface EventBindingsRuntimeCardProps {
  tenantIdForApi?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  /** 08.10.26 — внутри переключателя разделов шапка «Показать» не нужна. Не удалять. */
  embedded?: boolean;
  eventBindings: EventTemplateBindingItem[];
  eventBindingsLoading: boolean;
  templatesById: Record<string, Template>;
  onOpenTemplate: (templateId: string) => void;
}

const PREVIEW_SAMPLE_VALUES: Record<string, string> = {
  appointment_date: '12.04.2026',
  appointment_datetime: '12.04.2026 13:00',
  appointment_id: '00000000-0000-0000-0000-000000000111',
  appointment_time: '13:00',
  clinic_name: 'УлыбкаPRO',
  doctor_name: 'Елена Сергеевна',
  patient_fio: 'Наталья Игоревна',
  patient_name: 'Наталья Игоревна',
  phone: '79990001122',
  recipient_phone: '79990001122',
  service_name: 'Профессиональная гигиена',
};

function getPreviewSampleValue(variable: string, eventType?: string): string {
  const normalized = variable.trim().toLowerCase();
  if (normalized === 'event_type' && eventType) {
    return eventType;
  }
  if (normalized === 'broadcast_group' && eventType) {
    switch (eventType) {
      case 'appointment_reminder_24h':
        return 'reminder_24h';
      case 'appointment_reminder_2h':
        return 'reminder_2h';
      case 'appointment_reminder_advance':
        return 'reminder_advance';
      case 'feedback_request':
        return 'feedback_request';
      case 'birthday':
        return 'birthday';
      default:
        break;
    }
  }
  return PREVIEW_SAMPLE_VALUES[normalized] || `[${variable}]`;
}

function buildPreviewVariables(template?: Template, eventType?: string): Record<string, string> {
  const variables: Record<string, string> = {};
  template?.variables?.forEach((value) => {
    variables[value] = getPreviewSampleValue(value, eventType);
  });
  if (eventType) {
    variables.event_type = variables.event_type || eventType;
  }
  variables.phone = variables.phone || PREVIEW_SAMPLE_VALUES.phone;
  variables.patient_phone = variables.patient_phone || PREVIEW_SAMPLE_VALUES.phone;
  variables.recipient_phone = variables.recipient_phone || PREVIEW_SAMPLE_VALUES.phone;
  variables.appointment_id = variables.appointment_id || PREVIEW_SAMPLE_VALUES.appointment_id;
  return variables;
}

function getRuntimeSourceLabel(sourceKind?: string): string {
  switch (sourceKind) {
    case 'dedicated_override':
      return 'Точный channel override';
    case 'generic_binding':
      return 'Generic-привязка';
    case 'legacy_fallback':
      return 'Legacy fallback';
    default:
      return 'Шаблон не найден';
  }
}

export function EventBindingsRuntimeCard({
  tenantIdForApi,
  isCollapsed,
  onToggleCollapse,
  eventBindings,
  eventBindingsLoading,
  templatesById,
  onOpenTemplate,
  embedded = false,
}: EventBindingsRuntimeCardProps) {
  const [expandedPreviewKey, setExpandedPreviewKey] = useState<string | null>(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState<string | null>(null);
  const [previewCache, setPreviewCache] = useState<Record<string, TemplatePreviewResponse>>({});
  const [previewErrors, setPreviewErrors] = useState<Record<string, string>>({});
  const [resolvedTemplatesById, setResolvedTemplatesById] = useState<Record<string, Template>>({});

  const handleTogglePreview = async (
    previewKey: string,
    templateId?: string,
    eventType?: string,
    previewChannel?: string,
  ) => {
    if (expandedPreviewKey === previewKey) {
      setExpandedPreviewKey(null);
      return;
    }
    setExpandedPreviewKey(previewKey);
    if (!templateId || previewCache[previewKey] || previewLoadingKey === previewKey) {
      return;
    }
    setPreviewLoadingKey(previewKey);
    setPreviewErrors((current) => ({ ...current, [previewKey]: '' }));
    try {
      let template = resolvedTemplatesById[templateId] || templatesById[templateId];
      if (!template) {
        template = await templatesApi.get(templateId, tenantIdForApi);
        setResolvedTemplatesById((current) => ({ ...current, [templateId]: template! }));
      }
      const response = await templatesApi.preview(
        templateId,
        buildPreviewVariables(template, eventType),
        tenantIdForApi,
        previewChannel,
      );
      setPreviewCache((current) => ({ ...current, [previewKey]: response }));
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string } }; message?: string }).response?.data?.error ||
        (error as { message?: string }).message ||
        'Не удалось загрузить preview шаблона.';
      setPreviewErrors((current) => ({ ...current, [previewKey]: message }));
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const open = embedded || !isCollapsed;

  return (
    <Card className="border-shell-border bg-shell-surface shadow-none">
      {!embedded && (
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base font-extrabold tracking-tight text-shell-text">
            Фактическое распределение по каналам
          </CardTitle>
          <p className="max-w-3xl text-sm text-shell-muted">
            Здесь показано, какой шаблон реально сработает в runtime по каждому event type и каналу. Это матрица template binding, а не схема delivery fallback между каналами.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onToggleCollapse} className="shrink-0 font-semibold">
          {isCollapsed ? <ChevronRight className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
          {isCollapsed ? 'Показать' : 'Скрыть'}
        </Button>
      </CardHeader>
      )}
      {open && (
        <CardContent className="pt-0">
          {!tenantIdForApi ? (
            <EmptyState
              type="search"
              title="Выберите клинику"
              description="Для просмотра runtime-матрицы шаблонов выберите клинику."
            />
          ) : eventBindingsLoading ? (
            <LoadingState />
          ) : (
            <div className="space-y-2">
              {eventBindings.map((binding) => {
                const slots = [binding.generic_binding, ...(binding.channel_bindings || [])];
                return (
                  <div
                    key={binding.event_type}
                    className="space-y-3 rounded-2xl border border-shell-border bg-shell-elevated/40 p-3"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="info">{binding.label}</Badge>
                      <DispatchModeBadge mode={binding.dispatch_mode} />
                      <span className="font-mono text-xs text-shell-muted">{binding.event_type}</span>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      {slots.map((slot) => {
                        const runtime = slot.runtime;
                        const runtimeTemplate = runtime?.template;
                        const template = runtimeTemplate
                          ? resolvedTemplatesById[runtimeTemplate.id] || templatesById[runtimeTemplate.id]
                          : undefined;
                        const previewChannel = slot.channel || runtime?.source_channel;
                        const previewKey = `${binding.event_type}:${slot.channel || 'generic'}`;
                        const previewData = previewCache[previewKey];
                        const previewError = previewErrors[previewKey];
                        const previewExpanded = expandedPreviewKey === previewKey;

                        return (
                          <div
                            key={previewKey}
                            className="space-y-2 rounded-xl border border-shell-border bg-shell-surface p-3"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="secondary" className="gap-1.5 pl-1.5">
                                <ChannelName channelId={slot.channel} label={slot.label} size={14} />
                              </Badge>
                              <span className="text-xs text-shell-muted">
                                {getRuntimeSourceLabel(runtime?.source_kind)}
                              </span>
                            </div>

                            {runtimeTemplate ? (
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-shell-text">
                                  {runtimeTemplate.name}{' '}
                                  {runtimeTemplate.is_active ? '(активен)' : '(неактивен)'}
                                </div>
                                <div className="text-xs text-shell-muted">
                                  Источник: {runtime?.source_description || 'runtime resolve'}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => onOpenTemplate(runtimeTemplate.id)}
                                  >
                                    Открыть
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="h-7 px-2 text-xs"
                                    onClick={() =>
                                      void handleTogglePreview(
                                        previewKey,
                                        runtimeTemplate.id,
                                        binding.event_type,
                                        previewChannel,
                                      )
                                    }
                                  >
                                    <Eye className="mr-1 h-3.5 w-3.5" />
                                    {previewExpanded ? 'Скрыть preview' : 'Показать preview'}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-shell-warning">
                                Runtime не найдёт шаблон для этого канала, пока не появится dedicated
                                или generic привязка.
                              </div>
                            )}

                            {previewExpanded && (
                              <div className="rounded-xl border border-shell-border bg-shell-elevated/40 p-3">
                                {previewLoadingKey === previewKey ? (
                                  <LoadingState />
                                ) : previewError ? (
                                  <div className="text-xs text-shell-danger">{previewError}</div>
                                ) : previewData ? (
                                  <ResolvedTemplatePreview
                                    preview={previewData.preview}
                                    wabaPreview={previewData.waba_preview}
                                    buttons={previewData.buttons}
                                    variablesMissing={previewData.variables_missing}
                                    channel={previewChannel}
                                    isWabaTemplate={template?.template_type === 'waba_template'}
                                  />
                                ) : (
                                  <div className="text-xs text-shell-muted">
                                    Нажмите «Показать preview», чтобы увидеть текст шаблона.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
